import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import '@adornis/forms/x-input.js';
import '@adornis/popover/x-dropdown-selection.js';
import { ExamType } from '@ils/static/enums.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, of, switchMap } from 'rxjs';
import { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import { type Order, moveOrderManually } from '../../db/order.js';
import './ils-examday-select-item.js';

/**
 * @element ils-admin-move-order
 */
@customElement('ils-admin-move-order')
export class ILSAdminMoveOrder extends XDialog<Maybe<Order>> {
  static override get element_name() {
    return 'ils-admin-move-order';
  }
  @state() order = new RXController<Order | undefined>(this, undefined);
  @state() user = new RXController<ILSUser | undefined>(this, undefined);
  @state() orderSelector = { _id: 1 };

  @state() oldExamDayFromOrderProduct = new RXController(
    this,
    this.order.observable.pipe(
      switchMap(order => {
        return of(order?.products[0]?.product);
      }),
    ),
  );

  @state() days = new RXController<ExamDay[] | undefined>(this, undefined);
  @state() private readonly _futureAndJoinedDays = new RXController(
    this,
    combineLatest([this.user.observable, this.order.observable]).pipe(
      filter((data): data is [ILSUser, Order] => Boolean(data[0] && data[1])),
      switchMap(async ([user, order]) => {
        const days = await ExamDay.getFutureAndJoinedExamDays(
          user._id,
          order.products[0]?.product.examType ?? ExamType.FAMED,
        )({
          _id: 1,
          _class: 1,
          name: 1,
          candidateIDs: 1,
          maxCandidates: 1,
          location: { displayName: 1 },
        });
        return days.filter(exd => !exd.candidateIDs.includes(user._id));
      }),
    ),
  );

  @state() private selectFieldExamDay: ExamDay = new ExamDay({});

  @state() private readonly newExamDay = new RXController<ExamDay | undefined>(this, new ExamDay({}));

  get userId(): string {
    return this.user.value?._id || '';
  }

  get userName(): string {
    return this.user.value?.ecommerce?.fullName || this.user.value?.username || '';
  }

  private bookingDate = DateTime.now();

  override render() {
    return html`
      <x-flex space="md" padding="md" ${css({ maxWidth: this.sizes.maxReadableWidth })}>
        <x-h2>Order Verschieben</x-h2>
        <x-text>
          Du bist dabei die Order <b>${this.order.value?.orderNumber || this.order.value?.invoiceNumber || ''}</b> für
          Nutzer <b> ${this.userName} </b> zu verlegen. <br />
        </x-text>
        ${this.productSelector()}
        <x-input
          type="date"
          .value=${DateTime.now()}
          placeholder="Rechnungsdatum"
          @value-picked=${(e: ValueEvent<DateTime>) => {
            this.bookingDate = e.detail.value;
          }}
        ></x-input>
        <x-flex horizontal space="md" space-between>
          <x-button mode="outline" @click=${() => this.close(null)}> Abbrechen </x-button>

          <x-button ?disabled=${!this._canMoveOrder()} @click=${async () => this._moveOrder()}> Verschieben </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private _canMoveOrder() {
    if (!this.newExamDay.value?.name || !this.oldExamDayFromOrderProduct.value) return false;
    if (this.oldExamDayFromOrderProduct.value.name === this.newExamDay.value.name) return false;

    return true;
  }

  private async _moveOrder() {
    try {
      const result = await XNativeDialog.waitFor(
        moveOrderManually(
          this.order.value?._id || '',
          this.oldExamDayFromOrderProduct.value?.reference || '',
          this.newExamDay.value?._id || '',
          this.bookingDate,
          this.userId,
        )(this.orderSelector),
        'Order wird verschoben..',
      );

      void this.close(result);
    } catch (e) {
      logger.error(e);
    }
    void this.close(null);
  }

  private productSelector() {
    if (!this.selectFieldExamDay.name && this._futureAndJoinedDays.value?.[0]) {
      this.selectFieldExamDay = this._futureAndJoinedDays.value[0];
      this.newExamDay.next(this.selectFieldExamDay);
    }

    return html`
      <x-dropdown-selection
        .value=${this.newExamDay.value?.name ? this.newExamDay.value : this.selectFieldExamDay}
        placeholder="Prüfungstag"
        .selectables=${this._futureAndJoinedDays.value}
        .renderString=${(day: ExamDay) =>
          html`<ils-examday-select-item .examDay=${day} single-line></ils-examday-select-item>`}
        .renderItem=${(day: ExamDay) => html`<ils-examday-select-item .examDay=${day}></ils-examday-select-item>`}
        @value-picked=${async (e: ValueEvent<ExamDay>) => this.newExamDay.next(e.detail.value)}
      ></x-dropdown-selection>
    `;
  }
}
